import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import type { FieldValues, UseFormSetValue } from 'react-hook-form';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconGripVertical, IconChevronDown } from '@tabler/icons-react';
import useOnClickOutside from 'chat-ui/src/hooks/useClickoutside';
import { cn } from '@/lib/utils';
import RequiredLabel from '@/components/labels/RequiredLabel';
import { Textarea } from '@/components/ui/textarea';
import ScreeningTypesInfo from './ScreeningTypesInfo';
import { screeningQuestionTypeIcon, screeningQuestionTypeOptions } from './screeningQuestionTypes';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import ScreeningMultiAndSingleSelectAnswer from './ScreeningMultiAndSingleSelectAnswer';
import { Button } from '@/components/ui/button';
import useCreateProjectStore from '@/store/CreateProjectStore';
import { Media, ScreeningQuestionBoxProps, ScreeningResponse } from '@/types';
import { ScreeningQuestionType } from '@/enums';
import useTranslation from '@/hooks/useTranslation';
import QuestionMediaUploading from './QuestionMediaUploading';

function ScreeningQuestionBox({
  index,
  isActiveDrag,
  id,
  responses,
  openQuestionId,
  setOpenQuestionId,
  isUserViewer,
  isProjectCompleted,
  isProjectPublished,
  handleDeleteQuestion,
  question_text,
  type,
  register,
  handleQuestionTextChange,
  handleQuestionTypeChange,
  handleQuestionAnswerChange,
  clearAnswerError,
  handleDeleteAnswer,
  handleAnswersOrderChange,
  error,
  randomize,
  onRandomizeAnswersChange,
  quotasEnabled,
  onQuotasEnabledChange,
  onQuotaPercentageChange,
  currentQuestion,
  onMediaChange,
  isQuestionSaved,
}: ScreeningQuestionBoxProps) {
  const isNumeric = type === ScreeningQuestionType.NUMERIC;
  const isNumericAndResponses = isNumeric && responses && !Array.isArray(responses);
  const { lang } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const {
    isFormChanged,
    setIsFormChanged,
    setIsProjectCreateButtonDisabled,
    duplicatedScreeningQuestion,
  } = useCreateProjectStore();
  const isOpen = openQuestionId === id;
  const [questionType, setQuestionType] = useState(type);
  const [isQuestionTypeOpen, setIsQuestionTypeOpen] = useState(false);
  const [minimumValue, setMinimumValue] = useState(isNumericAndResponses ? responses.minimum : '');
  const [maximumValue, setMaximumValue] = useState(isNumericAndResponses ? responses.maximum : '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const handleQuestionTypeSelect = () => {
    setIsQuestionTypeOpen(!isQuestionTypeOpen);
  };
  const handleChangingQuestionType = (screenQuestionType: ScreeningQuestionType) => {
    setQuestionType(screenQuestionType);
    handleQuestionTypeChange(id, screenQuestionType);
    setIsFormChanged(true);
  };

  // QuestionMediaUploading persists label/reorder edits via an RHF setValue
  // on `${registrationPath}.media`. The screener editor's source of truth is
  // ScreeningQuestions' local state (which rewrites the whole
  // screening_questions form value on every change), so route those writes
  // through onMediaChange instead of the real form — a direct form write
  // would be clobbered by the next unrelated screener edit. TELV1-520
  // Intentionally narrow: writes to any path OTHER than `.media` are
  // deliberately dropped — QuestionMediaUploading only ever writes
  // `${registrationPath}.media`, and anything else would bypass the local
  // screener state that owns this form section. If the uploader ever grows a
  // new setValue path, extend this shim rather than passing the real form
  // setValue. The shouldDirty/shouldTouch options are also intentionally
  // ignored: dirtiness is applied downstream by updateQuestions'
  // setValue('screening_questions', …, { shouldDirty: true }).
  const mediaSetValue = ((name: unknown, value: unknown) => {
    if (typeof name === 'string' && name.endsWith('.media')) {
      onMediaChange(id, (value as Media[]) || []);
    }
  }) as UseFormSetValue<FieldValues>;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    border: isDragging ? '2px dashed #718096' : '1px solid #e2e8f0',
    opacity: isDragging ? 0.7 : 1,
    boxShadow: isDragging ? '0 4px 6px rgba(0, 0, 0, 0.4)' : 'none',
  };

  const toggleQuestion = () => {
    setOpenQuestionId(isOpen ? null : id);
  };

  useLayoutEffect(() => {
    if (isActiveDrag) {
      setOpenQuestionId(null);
    }
  }, [isActiveDrag, setOpenQuestionId]);

  useOnClickOutside(dropdownRef, (event) => {
    if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
      setIsQuestionTypeOpen(false);
    }
  });

  const isDisabled = isUserViewer || isProjectCompleted || isProjectPublished;
  // Quotas can still be toggled/edited on published projects (dynamic, recalculated on next screening check)
  const isQuotaToggleDisabled = isUserViewer || isProjectCompleted;
  const canDrag = !isUserViewer && !isProjectCompleted && !isProjectPublished && !isFormChanged;

  const quotaPercentageRawTotal =
    quotasEnabled && Array.isArray(responses)
      ? (responses as ScreeningResponse[])
          .filter((r) => r.rule === 'qualify')
          .reduce((sum, r) => sum + (Number(r.quota_percentage) || 0), 0)
      : 0;
  // Round to 3dp to avoid floating-point display issues (e.g. 99.99000000000001)
  const quotaPercentageTotal = Math.round(quotaPercentageRawTotal * 1000) / 1000;

  let errorMessage = null;
  if (duplicatedScreeningQuestion?.id === id) {
    errorMessage = lang.get('msg.questionAlreadyExists');
  } else if (error?.question?.message === 'msg.thisFieldIsRequired') {
    errorMessage = lang.get('msg.thisFieldIsRequired');
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col w-full p-4 bg-white border rounded-md shadow"
    >
      {/* Header */}
      <button
        type="button"
        className="flex items-center justify-between w-full px-5 py-1"
        onClick={toggleQuestion}
      >
        <h3 className="font-medium">
          {index + 1}. {lang.get('msg.question')}: {question_text}
        </h3>
        <div className="flex items-center gap-2">
          <span>
            <IconChevronDown
              size={20}
              className={`transition-all ease-in-out ${isOpen && 'rotate-180'}`}
            />
          </span>
          {/* drag item */}
          {canDrag && (
            <span className={`${!isOpen ? 'flex' : 'hidden'}`} {...attributes} {...listeners}>
              <IconGripVertical
                size={24}
                className="relative z-50 text-light-gray cursor-grabbing"
              />
            </span>
          )}
        </div>
      </button>

      {/* Body */}
      <div className={cn('flex flex-col w-full px-5 pt-6', openQuestionId !== id && 'hidden')}>
        {/* Question */}
        <RequiredLabel title="question">
          <Textarea
            {...register(`screening_questions.${index}.question`)}
            onChange={(e) => {
              setIsFormChanged(true);
              handleQuestionTextChange(id, e.target.value);
            }}
            disabled={isDisabled}
            defaultValue={question_text}
            className="min-h-16"
            placeholder={lang.get('msg.screeningQuestionPlaceholder')}
          />
          {errorMessage && (
            <p className="text-red-500 text-[12px] absolute -bottom-5 left-0">{errorMessage}</p>
          )}
        </RequiredLabel>

        {/* Stimuli (media) — same uploader as interview questions, scoped to
            the screening collection */}
        <div className="mt-7">
          <QuestionMediaUploading
            registrationPath={`screening_questions.${index}`}
            currentQuestion={currentQuestion}
            isProjectCompleted={isProjectCompleted}
            isProjectPublished={isProjectPublished}
            setValue={mediaSetValue}
            isQuestionSaved={isQuestionSaved}
            scope="screening"
          />
        </div>

        {/* Question type */}
        <div className="flex flex-col w-full gap-3 mt-7">
          {/* Custom select dropdown */}
          <div className="flex flex-col items-start w-full gap-2 text-[15px]">
            <div className="flex items-center justify-between w-full gap-2 font-medium">
              <p>{lang.get('msg.questionType')}</p>
              <ScreeningTypesInfo />
            </div>
            {/* Dropdown wrapper */}
            <div className="relative flex flex-col w-full">
              <input
                type="hidden"
                {...register(`screening_questions.${index}.type`)}
                value={questionType}
              />
              <button
                ref={buttonRef}
                disabled={isDisabled}
                onClick={() => {
                  handleQuestionTypeSelect();
                  setIsFormChanged(true);
                }}
                type="button"
                className={`flex items-center justify-between w-full px-4 py-2 rounded-md border border-natural-700 max-w-60 ${
                  isDisabled ? 'pointer-events-none opacity-50' : 'pointer-events-auto'
                } ${isQuestionTypeOpen ? 'rounded-b-none' : ''}`}
              >
                <div className="flex items-center gap-2 ">
                  {screeningQuestionTypeIcon[questionType as ScreeningQuestionType] as ReactNode}
                  <p>
                    {questionType === ScreeningQuestionType.MULTI_SELECT && 'Multi-select'}
                    {questionType === ScreeningQuestionType.SINGLE_SELECT && 'Single-select'}
                    {questionType === ScreeningQuestionType.NUMERIC && 'Numeric'}
                  </p>
                </div>
                <IconChevronDown size={16} />
              </button>
              {/* types */}
              <div
                ref={dropdownRef}
                className={cn(
                  'flex flex-col w-full max-w-60 z-100 absolute bg-white -bottom-[115px] rounded-b-md border-x border-natural-700 border-b overflow-hidden',
                  !isQuestionTypeOpen && 'hidden',
                )}
              >
                {screeningQuestionTypeOptions?.map((item) => (
                  <button
                    onClick={() => {
                      handleChangingQuestionType(item?.title);
                      handleQuestionTypeSelect();
                    }}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2',
                      questionType === item?.title && 'bg-primary text-white',
                      questionType !== item?.title && 'hover:bg-gray-100',
                    )}
                    type="button"
                    key={item.id}
                  >
                    {item?.icon}
                    <p>
                      {item?.title === ScreeningQuestionType.MULTI_SELECT && 'Multi-select'}
                      {item?.title === ScreeningQuestionType.SINGLE_SELECT && 'Single-select'}
                      {item?.title === ScreeningQuestionType.NUMERIC && 'Numeric'}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Quotas toggle — only for single-select */}
            {questionType === ScreeningQuestionType.SINGLE_SELECT && (
              <div className="flex items-center gap-3 mt-4">
                <Switch
                  checked={!!quotasEnabled}
                  onCheckedChange={(checked) => onQuotasEnabledChange(id, checked)}
                  disabled={isQuotaToggleDisabled}
                />
                <label className="text-sm font-medium">{lang.get('msg.enableQuotas')}</label>
              </div>
            )}
            {questionType === ScreeningQuestionType.MULTI_SELECT && (
              <div className="flex items-center gap-3 mt-4 opacity-50">
                <Switch checked={false} disabled />
                <label className="text-sm text-muted-foreground">
                  {lang.get('msg.enableQuotas')}
                </label>
                <span className="text-xs text-muted-foreground italic">
                  ({lang.get('msg.quotasComingSoon')})
                </span>
              </div>
            )}

            {/* Answers */}
            <div className="flex flex-col w-full gap-2 pb-5 mt-5">
              {/* Multi-select type */}
              {questionType === ScreeningQuestionType.MULTI_SELECT && (
                <ScreeningMultiAndSingleSelectAnswer
                  questionId={id}
                  responses={responses as ScreeningResponse[]}
                  isProjectCompleted={isProjectCompleted}
                  isProjectPublished={isProjectPublished}
                  itemType={questionType}
                  handleQuestionAnswerChange={handleQuestionAnswerChange}
                  clearAnswerError={clearAnswerError}
                  handleDeleteAnswer={handleDeleteAnswer}
                  handleAnswersOrderChange={handleAnswersOrderChange}
                  setIsFormChanged={setIsFormChanged}
                  error={error?.responses}
                  randomize={randomize}
                  onRandomizeAnswersChange={onRandomizeAnswersChange}
                />
              )}

              {/* Single-select type */}
              {questionType === ScreeningQuestionType.SINGLE_SELECT && (
                <ScreeningMultiAndSingleSelectAnswer
                  questionId={id}
                  responses={responses as ScreeningResponse[]}
                  isProjectCompleted={isProjectCompleted}
                  isProjectPublished={isProjectPublished}
                  itemType={questionType}
                  handleQuestionAnswerChange={handleQuestionAnswerChange}
                  clearAnswerError={clearAnswerError}
                  handleDeleteAnswer={handleDeleteAnswer}
                  handleAnswersOrderChange={handleAnswersOrderChange}
                  setIsFormChanged={setIsFormChanged}
                  error={error?.responses}
                  randomize={randomize}
                  onRandomizeAnswersChange={onRandomizeAnswersChange}
                  quotasEnabled={quotasEnabled}
                  onQuotaPercentageChange={onQuotaPercentageChange}
                />
              )}

              {/* Numeric type */}
              {ScreeningQuestionType.NUMERIC === questionType && (
                <div className="flex flex-col w-full gap-5">
                  <div className="flex flex-col w-full gap-2">
                    <div className="flex items-center justify-between w-full gap-8">
                      <RequiredLabel title="minimum" notRequired className="w-full">
                        <Input
                          disabled={isDisabled}
                          {...register(`screening_questions.${index}.numericResponse.minimum`, {
                            valueAsNumber: true,
                          })}
                          type="number"
                          placeholder="e.g. 2"
                          value={minimumValue}
                          className={validationError ? 'border-red-500' : ''}
                          onChange={(e) => {
                            setIsFormChanged(true);
                            const newMinValue = +e.target.value;
                            setMinimumValue(newMinValue);
                            const isError = !!maximumValue && newMinValue > +maximumValue;
                            setIsProjectCreateButtonDisabled(isError);
                            setValidationError(
                              isError ? 'Minimum value cannot be greater than maximum value' : null,
                            );

                            handleQuestionAnswerChange(id, '', {
                              minimum: newMinValue,
                            });
                          }}
                        />
                      </RequiredLabel>
                      <RequiredLabel title="maximum" notRequired className="w-full">
                        <Input
                          disabled={isDisabled}
                          {...register(`screening_questions.${index}.numericResponse.maximum`, {
                            valueAsNumber: true,
                          })}
                          type="number"
                          placeholder="e.g. 4"
                          value={maximumValue}
                          className={validationError ? 'border-red-500' : ''}
                          onChange={(e) => {
                            setIsFormChanged(true);
                            const newMaxValue = +e.target.value;
                            setMaximumValue(newMaxValue);
                            const isError = !!minimumValue && +minimumValue > newMaxValue;
                            setIsProjectCreateButtonDisabled(isError);
                            setValidationError(
                              isError ? 'Maximum value cannot be less than minimum value' : null,
                            );
                            handleQuestionAnswerChange(id, '', {
                              maximum: newMaxValue,
                            });
                          }}
                        />
                      </RequiredLabel>
                    </div>
                    {validationError && <p className="text-red-500 text-xs">{validationError}</p>}
                  </div>
                </div>
              )}
            </div>

            {/* Quota percentage total */}
            {quotasEnabled && questionType === ScreeningQuestionType.SINGLE_SELECT && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium">{lang.get('msg.quotaTotal')}:</span>
                <span
                  className={cn(
                    'text-sm font-semibold',
                    quotaPercentageTotal === 100 ? 'text-green-600' : 'text-red-500',
                  )}
                >
                  {quotaPercentageTotal}% / 100%
                </span>
                {quotaPercentageTotal !== 100 && (
                  <span className="text-xs text-red-500">
                    {lang.get('msg.quotaPercentagesMustEqual100')}
                  </span>
                )}
              </div>
            )}

            {/* delete question */}
            <Button
              disabled={isDisabled}
              onClick={() => handleDeleteQuestion(id)}
              variant="destructive"
              className="self-end"
            >
              {lang.get('msg.deleteQuestion')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScreeningQuestionBox;
